/**
 * The FeatureModule contract — the single extension point of the app.
 *
 * Adding a feature = (1) create src/features/<id>/, (2) export a FeatureModule from its index, and
 * (3) add that one import to the `FEATURES` array in src/app/registry.ts. The router + side-nav +
 * command palette compose themselves from the registry. A feature NEVER edits the router, the shell,
 * or another feature.
 *
 * Capability gating is declarative: list `requiredCapabilities` on the module (gates the whole
 * nav group) and/or per-route via `requiredCapabilities` on a NavItem. The router wraps every
 * feature route in <ProtectedRoute> using these — features write zero auth-guard code.
 */
import type { ReactNode } from "react";
import type { RouteObject } from "react-router-dom";

/** A side-nav entry. `path` must match a route the module declares. */
export interface NavItem {
  /** Stable id (also the i18n key suffix if you follow nav.<id>). */
  id: string;
  /** Absolute app path (e.g. "/reception"). */
  path: string;
  /** Label — pass an i18n key and the shell resolves it, or a literal ReactNode. */
  label: ReactNode;
  /** Optional monochrome icon (ReactNode; keep it a simple glyph/SVG, token-colored). */
  icon?: ReactNode;
  /** Capabilities ALL required to SEE this item (subset/override of the module's). */
  requiredCapabilities?: ReadonlyArray<string>;
  /** Group ordering hint within the nav (lower = higher). Default 100. */
  order?: number;
  /** Optional section heading this item sits under (e.g. "Production", "Finance"). */
  section?: string;
}

/**
 * The contract every feature module exports.
 *
 *   const receptionModule: FeatureModule = {
 *     id: "reception",
 *     title: "Réception",
 *     requiredCapabilities: ["reception.dossier.read"],
 *     routes: [{ path: "/reception", element: <ReceptionPage /> }],
 *     nav: [{ id: "reception", path: "/reception", label: "nav.reception", order: 20 }],
 *   };
 *   export default receptionModule;
 */
export interface FeatureModule {
  /** Unique module id (kebab/lowercase; matches the server module + the src/features folder). */
  id: string;
  /** Human title (FR) — used in the command palette group + as an i18n fallback. */
  title: string;
  /** React Router v6 route objects, mounted UNDER the authenticated shell layout. */
  routes: RouteObject[];
  /** Side-nav entries this module contributes. */
  nav: NavItem[];
  /** Capabilities ALL required for the module as a whole (gates its nav group + routes). */
  requiredCapabilities: ReadonlyArray<string>;
  /** Optional: called once when the module is registered (e.g. registerMessages for i18n). */
  onRegister?: () => void;
}

/** Type guard used by the registry loader to fail loudly on a malformed module. */
export function assertFeatureModule(m: unknown): asserts m is FeatureModule {
  const fm = m as Partial<FeatureModule> | null;
  if (
    !fm ||
    typeof fm.id !== "string" ||
    typeof fm.title !== "string" ||
    !Array.isArray(fm.routes) ||
    !Array.isArray(fm.nav) ||
    !Array.isArray(fm.requiredCapabilities)
  ) {
    throw new Error(
      `Invalid FeatureModule: ${JSON.stringify(fm?.id ?? m)} — must have { id, title, routes[], nav[], requiredCapabilities[] }.`,
    );
  }
}
